import { DateTime, Settings } from "luxon";

/**
 * Frontend timezone service for managing salon-specific timezone handling
 */
let _currentSalonTimezone: string | null = null;
let _currentSalonId: string | null = null;

export const TimezoneService = {
  /**
   * Set the global default timezone for Luxon based on salon data
   * This affects all DateTime operations in the current context
   */
  setSalonTimezone(timezone: string, salonId?: string): void {
    // Validate the timezone
    const testDateTime = DateTime.local().setZone(timezone);
    if (!testDateTime.isValid) {
      throw new Error(`Invalid timezone: ${timezone}`);
    }

    // Set the global default timezone
    Settings.defaultZone = timezone;
    _currentSalonTimezone = timezone;
    _currentSalonId = salonId || null;
  },

  /**
   * Get the current salon's timezone
   */
  getCurrentSalonTimezone(): string {
    return _currentSalonTimezone || Settings.defaultZone.name;
  },

  /**
   * Get the current salon ID
   */
  getCurrentSalonId(): string | null {
    return _currentSalonId;
  },

  /**
   * Convert a DateTime to the current salon's timezone
   */
  convertToSalonTimezone(dateTime: DateTime): DateTime {
    const timezone = TimezoneService.getCurrentSalonTimezone();
    return dateTime.setZone(timezone);
  },

  /**
   * Get current time in the current salon's timezone
   */
  getCurrentTimeInSalonTimezone(): DateTime {
    const timezone = TimezoneService.getCurrentSalonTimezone();
    return DateTime.now().setZone(timezone);
  },

  /**
   * Check if a timezone is valid
   */
  isValidTimezone(timezone: string): boolean {
    const testDateTime = DateTime.local().setZone(timezone);
    return testDateTime.isValid;
  },

  /**
   * Reset the global timezone to UTC
   */
  resetToUTC(): void {
    Settings.defaultZone = "UTC";
    _currentSalonTimezone = "UTC";
    _currentSalonId = null;
  },

  /**
   * Get the current global default timezone
   */
  getCurrentDefaultTimezone(): string {
    return Settings.defaultZone.name;
  },

  /**
   * Parse a time string (HH:MM) in the current salon's timezone
   */
  parseTimeInSalonTimezone(timeString: string, date?: DateTime): DateTime {
    const timezone = TimezoneService.getCurrentSalonTimezone();
    const baseDate = date || DateTime.now().setZone(timezone);

    const [hours, minutes] = timeString.split(":").map(Number);
    return baseDate.set({
      hour: hours,
      minute: minutes,
      second: 0,
      millisecond: 0,
    });
  },

  /**
   * Format a DateTime to a time string (HH:MM) in the current salon's timezone
   */
  formatTimeInSalonTimezone(dateTime: DateTime): string {
    const timezone = TimezoneService.getCurrentSalonTimezone();
    return dateTime.setZone(timezone).toFormat("HH:mm");
  },

  /**
   * Format a DateTime to a date string in the current salon's timezone
   */
  formatDateInSalonTimezone(dateTime: DateTime, format: string = "yyyy-MM-dd"): string {
    const timezone = TimezoneService.getCurrentSalonTimezone();
    return dateTime.setZone(timezone).toFormat(format);
  },

  /**
   * Format a DateTime to a full date-time string in the current salon's timezone
   */
  formatDateTimeInSalonTimezone(dateTime: DateTime, format: string = "yyyy-MM-dd HH:mm"): string {
    const timezone = TimezoneService.getCurrentSalonTimezone();
    return dateTime.setZone(timezone).toFormat(format);
  },

  /**
   * Parse a date string in the current salon's timezone
   */
  parseDateInSalonTimezone(dateString: string, format: string = "yyyy-MM-dd"): DateTime {
    const timezone = TimezoneService.getCurrentSalonTimezone();
    return DateTime.fromFormat(dateString, format, { zone: timezone });
  },

  /**
   * Parse a date-time string in the current salon's timezone
   */
  parseDateTimeInSalonTimezone(
    dateTimeString: string,
    format: string = "yyyy-MM-dd HH:mm"
  ): DateTime {
    const timezone = TimezoneService.getCurrentSalonTimezone();
    return DateTime.fromFormat(dateTimeString, format, { zone: timezone });
  },

  /**
   * Get the timezone abbreviation for display
   */
  getTimezoneAbbreviation(): string {
    const timezone = TimezoneService.getCurrentSalonTimezone();
    return DateTime.now().setZone(timezone).toFormat("ZZZZ");
  },

  /**
   * Get the timezone offset for display
   */
  getTimezoneOffset(): string {
    const timezone = TimezoneService.getCurrentSalonTimezone();
    return DateTime.now().setZone(timezone).toFormat("ZZ");
  },

  /**
   * Check if the current salon is in daylight saving time
   */
  isInDaylightSavingTime(): boolean {
    const timezone = TimezoneService.getCurrentSalonTimezone();
    return DateTime.now().setZone(timezone).isInDST;
  },

  /**
   * Get user-friendly timezone display name
   */
  getTimezoneDisplayName(
    timezone?: string,
    language?: string
  ): {
    friendlyName: string;
    technicalName: string;
  } {
    const targetTimezone = timezone || TimezoneService.getCurrentSalonTimezone();
    const locale = language === "vi" ? "vi-VN" : language === "de" ? "de-DE" : "en-US";

    try {
      // Use Intl.DateTimeFormat to get user-friendly timezone name
      const formatter = new Intl.DateTimeFormat(locale, {
        timeZone: targetTimezone,
        timeZoneName: "long",
      });

      // Get the timezone name from the formatted parts
      const parts = formatter.formatToParts(new Date());
      const timeZonePart = parts.find((part) => part.type === "timeZoneName");
      const friendlyName = timeZonePart?.value || targetTimezone;

      return {
        friendlyName,
        technicalName: targetTimezone,
      };
    } catch (error) {
      // Fallback to technical name if formatting fails
      console.warn(`Failed to format timezone ${targetTimezone}:`, error);
      return {
        friendlyName: targetTimezone,
        technicalName: targetTimezone,
      };
    }
  },
};

/**
 * Utility functions for common date/time operations
 */
export const timezoneUtils = {
  /**
   * Create a DateTime from a JavaScript Date in the salon's timezone
   */
  fromJSDate: (date: Date): DateTime => {
    return DateTime.fromJSDate(date).setZone(TimezoneService.getCurrentSalonTimezone());
  },

  /**
   * Create a DateTime from an ISO string in the salon's timezone
   */
  fromISO: (iso: string): DateTime => {
    return DateTime.fromISO(iso).setZone(TimezoneService.getCurrentSalonTimezone());
  },

  /**
   * Create a DateTime from SQL timestamp in the salon's timezone
   */
  fromSQL: (sql: string): DateTime => {
    return DateTime.fromSQL(sql).setZone(TimezoneService.getCurrentSalonTimezone());
  },

  /**
   * Get the start of day in the salon's timezone
   */
  startOfDay: (date?: DateTime): DateTime => {
    const timezone = TimezoneService.getCurrentSalonTimezone();
    const baseDate = date || DateTime.now().setZone(timezone);
    return baseDate.startOf("day");
  },

  /**
   * Get the end of day in the salon's timezone
   */
  endOfDay: (date?: DateTime): DateTime => {
    const timezone = TimezoneService.getCurrentSalonTimezone();
    const baseDate = date || DateTime.now().setZone(timezone);
    return baseDate.endOf("day");
  },

  /**
   * Get the start of week in the salon's timezone
   */
  startOfWeek: (date?: DateTime): DateTime => {
    const timezone = TimezoneService.getCurrentSalonTimezone();
    const baseDate = date || DateTime.now().setZone(timezone);
    return baseDate.startOf("week");
  },

  /**
   * Get the end of week in the salon's timezone
   */
  endOfWeek: (date?: DateTime): DateTime => {
    const timezone = TimezoneService.getCurrentSalonTimezone();
    const baseDate = date || DateTime.now().setZone(timezone);
    return baseDate.endOf("week");
  },

  /**
   * Get the start of month in the salon's timezone
   */
  startOfMonth: (date?: DateTime): DateTime => {
    const timezone = TimezoneService.getCurrentSalonTimezone();
    const baseDate = date || DateTime.now().setZone(timezone);
    return baseDate.startOf("month");
  },

  /**
   * Get the end of month in the salon's timezone
   */
  endOfMonth: (date?: DateTime): DateTime => {
    const timezone = TimezoneService.getCurrentSalonTimezone();
    const baseDate = date || DateTime.now().setZone(timezone);
    return baseDate.endOf("month");
  },
};
